import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TextInput, TouchableOpacity, Image } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Repeat, Globe, Share2, ExternalLink, ArrowLeft } from 'lucide-react';
import TweetActionsManager from '../services/TweetActionsManager';
import ListsManager from '../services/ListsManager';
import { useTranslation } from 'react-i18next';

const TweetItem = ({ tweet, isMainTweet = false, indentLevel = 0, onReply, onQuote, onShare, onTranslate, onAuthorPress, onOpenUrl }) => {
  const isFromTelegram = tweet.source === 'telegram';

  return (
    <View style={[styles.tweetContainer, { marginLeft: indentLevel * 20 }]}>
      {isMainTweet && <View style={styles.mainTweetIndicator} />}

      <View style={styles.tweetHeader}>
        <TouchableOpacity style={styles.userInfo} onPress={() => onAuthorPress(tweet.author_name, isFromTelegram)}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{tweet.author_name ? tweet.author_name.charAt(0).toUpperCase() : '?'}</Text>
          </View>
          <View style={styles.nameInfo}>
            <Text style={styles.userName}>{tweet.author_name || 'Unknown'}</Text>
            <Text style={styles.userHandle}>{tweet.username || ''}</Text>
          </View>
        </TouchableOpacity>
        <Text style={styles.timestamp}>{tweet.localCreatedAt || tweet.created_at || 'Unknown time'}</Text>
      </View>

      <TouchableOpacity
        style={styles.tweetContent}
        onPress={() => onOpenUrl(tweet.url)}
      >
        <Text style={styles.tweetText}>{tweet.tweet_content}</Text>

        {tweet.tweet_images && tweet.tweet_images.length > 0 && (
          <View style={styles.imagesContainer}>
            {tweet.tweet_images.map((image, index) => (
              <TouchableOpacity key={index} style={styles.imageWrapper} onPress={() => onOpenUrl(image)}>
                <Image
                  source={{ uri: image }}
                  style={styles.tweetImage}
                  resizeMode="cover"
                  onError={() => console.log(`Failed to load image: ${image}`)}
                />
              </TouchableOpacity>
            ))}
          </View>
        )}
      </TouchableOpacity>

      <View style={styles.tweetActions}>
        {onReply && (
          <TouchableOpacity style={styles.actionButton} onPress={() => onReply(tweet)}>
            <MessageSquare size={18} color="#888" />
          </TouchableOpacity>
        )}

        {onQuote && (
          <TouchableOpacity style={styles.actionButton} onPress={() => onQuote(tweet)}>
            <Repeat size={18} color="#888" />
          </TouchableOpacity>
        )}

        {onTranslate && (
          <TouchableOpacity style={styles.actionButton} onPress={() => onTranslate(tweet)}>
            <Globe size={18} color="#888" />
          </TouchableOpacity>
        )}

        <TouchableOpacity style={styles.actionButton} onPress={() => onShare(tweet.url)}>
          <Share2 size={18} color="#888" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.actionButton} onPress={() => onOpenUrl(tweet.url)}>
          <ExternalLink size={18} color="#888" />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const ReplyThreadScreen = () => {
  const { tweetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  // Get thread data from location state
  const { dependencyTweets = [], originalTweet, isQuoting = false } = location.state || {};

  const handleBack = () => {
    navigate(-1);
  };

  const handleReplyPress = (tweet) => {
    TweetActionsManager.getInstance().handleReplyTapped(tweet, { navigate });
  };

  const handleQuotePress = (tweet) => {
    TweetActionsManager.getInstance().handleQuoteTapped(tweet, { navigate });
  };

  const handleAuthorPress = (username, isFromTelegram) => {
    TweetActionsManager.getInstance().handleAuthorNamePress(username, isFromTelegram, { navigate });
  };

  const handleTranslate = (tweet) => {
    TweetActionsManager.getInstance().handleTranslateButtonTapped(tweet, { navigate });
  };

  const handleShare = (url) => {
    TweetActionsManager.getInstance().handleShareButtonTapped(url);
  };

  const handleOpenUrl = (url) => {
    TweetActionsManager.getInstance().handleOpenButtonTapped(url, { navigate });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loadingText}>{t('app.replyThread.loading')}</Text>
      </View>
    );
  }

  // If no original tweet in state, show error
  if (!originalTweet) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack}>
            <Text style={styles.backButton}>{t('app.replyThread.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('app.replyThread.title')}</Text>
          <View style={{ width: 50 }} />
        </View>

        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{t('app.replyThread.notFound')}</Text>
          <TouchableOpacity onPress={handleBack} style={styles.returnButton}>
            <Text style={styles.returnButtonText}>{t('app.replyThread.returnTimeline')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack}>
          <Text style={styles.backButton}>{t('app.replyThread.back')}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('app.replyThread.title')}</Text>
        <View style={{ width: 50 }} />
      </View>

      <ScrollView style={styles.content}>
        {/* Show dependency tweets first if not quoting */}
        {!isQuoting && dependencyTweets.map((tweet, index) => (
          <TweetItem
            key={`dep-${index}`}
            tweet={tweet}
            indentLevel={0}
            onShare={handleShare}
            onOpenUrl={handleOpenUrl}
            onAuthorPress={handleAuthorPress}
          />
        ))}

        {/* Show original tweet */}
        <TweetItem
          tweet={originalTweet}
          isMainTweet={true}
          indentLevel={isQuoting ? 0 : 1}
          onReply={handleReplyPress}
          onQuote={handleQuotePress}
          onShare={handleShare}
          onTranslate={handleTranslate}
          onOpenUrl={handleOpenUrl}
          onAuthorPress={handleAuthorPress}
        />

        {/* Show dependency tweets after if quoting */}
        {isQuoting && dependencyTweets.map((tweet, index) => (
          <TweetItem
            key={`dep-${index}`}
            tweet={tweet}
            indentLevel={1}
            onShare={handleShare}
            onOpenUrl={handleOpenUrl}
            onAuthorPress={handleAuthorPress}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    zIndex: 10,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  backButton: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'all 0.2s',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: '-0.01em',
    fontFamily: 'Outfit, sans-serif',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--bg-deep-void)',
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  content: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyText: {
    color: 'var(--text-muted)',
    fontSize: 16,
    marginBottom: 24,
  },
  returnButton: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 24,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  returnButtonText: {
    color: 'var(--text-primary)',
    fontWeight: '600',
    fontSize: 16,
  },
  tweetContainer: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    position: 'relative',
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    transition: 'background-color 0.2s',
  },
  mainTweetIndicator: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: 'var(--accent-primary)',
    boxShadow: '2px 0 8px rgba(59, 130, 246, 0.5)',
  },
  tweetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    alignItems: 'center',
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  avatarText: {
    color: 'var(--accent-primary)',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
  },
  nameInfo: {
    flexDirection: 'column',
  },
  userName: {
    color: 'var(--text-primary)',
    fontWeight: '700',
    marginRight: 6,
    fontSize: 15,
  },
  userHandle: {
    color: 'var(--text-secondary)',
    fontSize: 13,
  },
  timestamp: {
    color: 'var(--text-muted)',
    fontSize: 13,
  },
  tweetContent: {
    marginLeft: 56,
  },
  tweetText: {
    color: 'var(--text-primary)',
    fontSize: 16,
    marginBottom: 12,
    lineHeight: 24,
    fontFamily: 'Inter, sans-serif',
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
    marginBottom: 16,
    gap: 8,
  },
  imageWrapper: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    overflow: 'hidden',
    width: 150,
    height: 150,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  tweetImage: {
    width: '100%',
    height: '100%',
    opacity: 0.9,
    transition: 'opacity 0.2s',
  },
  tweetActions: {
    flexDirection: 'row',
    marginLeft: 56,
    marginTop: 8,
  },
  actionButton: {
    marginRight: 24,
    padding: 8,
    borderRadius: 12,
    backgroundColor: 'transparent',
    transition: 'all 0.2s',
    cursor: 'pointer',
    opacity: 0.7,
  },
});

export default ReplyThreadScreen; 